// services/apiService.ts
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { captureException } from '@/posthog-analytics/posthog';

export function getCsrfToken() {
  const name = 'csrftoken';
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith(name + '=')) {
      return cookie.substring(name.length + 1);
    }
  }
  return null;
}

export async function fetchWithCsrf<T = any>(url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
  try {
    const csrfToken = getCsrfToken();
    const isFormData = config.data instanceof FormData;
    const finalConfig: AxiosRequestConfig = {
      ...config,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...config.headers,
        'X-CSRFToken': csrfToken,
      },
      withCredentials: true,
      timeout: 90000, // Need to revert it back to 30s
    };

    const response: AxiosResponse<T> = await axios(url, finalConfig);
    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      captureException(error, {
        extra: {
          requestConfig: error.config,
          responseStatus: error.response?.status,
          responseData: error.response?.data,
        },
        tags: {
          api_url: url,
          api_status_code: error.response?.status || 0,
        },
      });
      console.error(
        `Server responded with an error: ${url}:`,
        {
          status: error.response?.status,
          data: error.response?.data,
          config: error.config,
        }
      );
      throw error.response ?? new Error('Unexpected error occurred');
    } else {
      captureException(error);
      console.error('A network error occurred:', (error as Error).message);
      throw new Error('Network error or server is unreachable.');
    }
  }
}

// Create Axios instance with base configuration
const api = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // To send cookies
});

// Interceptor to attach CSRF token to each request
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const csrfToken = getCsrfToken();
  if (csrfToken && config.headers) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  return config;
});

// Interceptor to handle responses
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    const { response } = error;
    if (response && response.status === 401) {
      // window.location.href = '/login'; // Redirect to login
    }
    return Promise.reject(error);
  },
);

// Generic GET request with improved error handling
export const getRequest = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await api.get<T>(url, config);
    return response.data;
  } catch (error) {
    handleError(error as AxiosError); // Custom error handling function
    throw error;
  }
};

// Generic POST request with improved error handling
export const postRequest = async <T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig,
): Promise<T> => {
  try {
    // Support different HTTP methods via config
    const method = config?.method?.toLowerCase() || 'post';
    let response: AxiosResponse<T>;
    
    switch (method) {
      case 'put':
        response = await api.put<T>(url, data, config);
        break;
      case 'patch':
        response = await api.patch<T>(url, data, config);
        break;
      case 'delete':
        response = await api.delete<T>(url, { ...config, data });
        break;
      default:
        response = await api.post<T>(url, data, config);
        break;
    }
    
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      console.log(error?.response?.status === 400);
      throw error;
    } else {
      handleError(error as AxiosError); // Custom error handling function
      throw error;
    }
  }
};

// Better error handling function
const handleError = (error: AxiosError): void => {
  if (error.response) {
    console.error(
      `API Error: ${error.response.status} - ${error.response.statusText}`,
    );
  } else if (error.request) {
    console.error('No response from API:', error.request);
  } else {
    console.error('Error occurred:', error.message);
  }
};
